/**
 * RecordsSidebar - Records Section Navigation Sidebar
 *
 * Features:
 * - 2-column grid navigation for quick access to records pages
 * - Visual separator between nav and unit list
 * - Filtered unit list that responds to FilterContext
 * - Collapsible state with icon-only mode
 */

import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Truck,
  Wrench,
  ClipboardList,
  History,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Cpu,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { useFilters } from '@/contexts/FilterContext';
import { authenticatedFetch } from '@/lib/api';
import { RecordsUnitList } from './RecordsUnitList';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface NavItem {
  name: string;
  shortName: string; // Abbreviated name for grid display
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeKey?: 'criticalUnits' | 'expiringRegistrations' | 'overdueCount' | 'openWorkOrders' | 'documentGaps';
  badgeColor?: 'red' | 'yellow' | 'default';
}

const navItems: NavItem[] = [
  {
    name: 'Dashboard',
    shortName: 'Dashboard',
    path: '/records',
    icon: LayoutDashboard,
  },
  {
    name: 'Unit Inventory',
    shortName: 'Inventory',
    path: '/records/inventory',
    icon: Truck,
    badgeKey: 'criticalUnits',
    badgeColor: 'red',
  },
  {
    name: 'Files',
    shortName: 'Files',
    path: '/records/files',
    icon: FolderOpen,
    badgeKey: 'documentGaps',
    badgeColor: 'red',
  },
  {
    name: 'Maintenance',
    shortName: 'Maint.',
    path: '/records/maintenance',
    icon: Wrench,
    badgeKey: 'overdueCount',
    badgeColor: 'red',
  },
  {
    name: 'Work Orders',
    shortName: 'Orders',
    path: '/records/work-orders',
    icon: ClipboardList,
    badgeKey: 'openWorkOrders',
    badgeColor: 'default',
  },
  {
    name: 'Damage',
    shortName: 'Damage',
    path: '/records/damage',
    icon: AlertTriangle,
  },
  {
    name: 'Check-Ins',
    shortName: 'Check-Ins',
    path: '/records/check-ins',
    icon: History,
  },
];

interface RecordsSidebarProps {
  collapsed: boolean;
  onToggle: (collapsed: boolean) => void;
}

interface BadgeCounts {
  documentGaps: number;
  criticalUnits: number;
  expiringRegistrations: number;
  overdueCount: number;
  openWorkOrders: number;
}

const initialCounts: BadgeCounts = {
  documentGaps: 0,
  criticalUnits: 0,
  expiringRegistrations: 0,
  overdueCount: 0,
  openWorkOrders: 0,
};

export function RecordsSidebar({ collapsed, onToggle }: RecordsSidebarProps) {
  const location = useLocation();
  const { user, loading } = useSettingsContext();
  const { selectedCompanyIds, selectedGroupIds } = useFilters();
  const [counts, setCounts] = useState<BadgeCounts>(initialCounts);

  // Fetch badge counts from API - re-fetch when filters change
  useEffect(() => {
    if (loading || !user || !user.is_internal) {
      setCounts(initialCounts);
      return;
    }

    async function fetchCounts() {
      try {
        // Build query params for filters
        const params = new URLSearchParams();
        if (selectedCompanyIds.length > 0) {
          params.set('company_ids', selectedCompanyIds.join(','));
        }
        if (selectedGroupIds.length > 0) {
          params.set('group_ids', selectedGroupIds.join(','));
        }
        const queryString = params.toString();
        const url = `/api/v1/admin/records/dashboard/counts${queryString ? `?${queryString}` : ''}`;

        const response = await authenticatedFetch(url);
        if (response.ok) {
          const data = await response.json();
          setCounts({
            documentGaps: data.documentGaps || 0,
            criticalUnits: data.criticalUnits || 0,
            expiringRegistrations: data.expiringRegistrations || 0,
            overdueCount: data.overdueCount || 0,
            openWorkOrders: data.openWorkOrders || 0,
          });
        } else if (response.status === 403) {
          // Non-internal users should never see admin counts; clear stale badges.
          setCounts(initialCounts);
        }
      } catch (error) {
        // Log error for debugging
        console.error('[RecordsSidebar] Failed to fetch counts:', error);
        // If a 403 is thrown upstream (e.g. proxy/middleware), clear stale badges.
        if (error instanceof Error && error.message.includes('403')) {
          setCounts(initialCounts);
        }
      }
    }

    fetchCounts();
  }, [loading, user, selectedCompanyIds, selectedGroupIds]);

  const isExpanded = !collapsed;

  const getBadgeColorClass = (color: 'red' | 'yellow' | 'default' | undefined) => {
    if (color === 'red') return 'bg-red-500';
    if (color === 'yellow') return 'bg-yellow-500';
    return 'bg-[#8FB560]';
  };

  const getBadgeClasses = (color: 'red' | 'yellow' | 'default' | undefined, isActive: boolean) => {
    if (color === 'red') {
      return isActive
        ? 'bg-red-500/20 border-red-500/50 text-red-400'
        : 'bg-red-500/10 border-red-500/30 text-red-400';
    }
    if (color === 'yellow') {
      return isActive
        ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
        : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400';
    }
    return isActive
      ? 'bg-[#8FB560]/20 border-[#8FB560]/50 text-[#8FB560]'
      : 'bg-muted/50 border-border/50 text-muted-foreground';
  };

  // Render collapsed nav item with tooltip
  const renderCollapsedNavItem = (item: NavItem) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    const badgeCount = item.badgeKey ? counts[item.badgeKey] : 0;

    return (
      <Tooltip key={item.path}>
        <TooltipTrigger asChild>
          <Link
            to={item.path}
            className={cn(
              'relative flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200',
              isActive
                ? 'bg-[#8FB560]/10 text-[#8FB560]'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
          >
            <Icon className={cn('w-5 h-5', isActive && 'text-[#8FB560]')} />
            {/* Badge dot indicator */}
            {badgeCount > 0 && (
              <div
                className={cn(
                  'absolute top-1 right-1 w-2 h-2 rounded-full border border-card',
                  getBadgeColorClass(item.badgeColor)
                )}
              />
            )}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" className="flex items-center gap-2">
          <span>{item.name}</span>
          {badgeCount > 0 && (
            <span
              className={cn(
                'px-1.5 py-0.5 text-xs rounded-full border',
                getBadgeClasses(item.badgeColor, false)
              )}
            >
              {badgeCount}
            </span>
          )}
        </TooltipContent>
      </Tooltip>
    );
  };

  // Render expanded nav item in 2-column grid
  const renderExpandedNavItem = (item: NavItem) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    const badgeCount = item.badgeKey ? counts[item.badgeKey] : 0;

    return (
      <Link
        key={item.path}
        to={item.path}
        className={cn(
          'relative flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg transition-all duration-200 min-h-[72px]',
          isActive
            ? 'bg-[#8FB560]/10 text-[#8FB560] ring-1 ring-[#8FB560]/30'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
        )}
      >
        {/* Icon with badge */}
        <div className="relative">
          <Icon className={cn('w-5 h-5', isActive && 'text-[#8FB560]')} />
          {/* Badge dot indicator on icon */}
          {badgeCount > 0 && (
            <div
              className={cn(
                'absolute -top-1 -right-1.5 w-2 h-2 rounded-full border border-card',
                getBadgeColorClass(item.badgeColor)
              )}
            />
          )}
        </div>

        {/* Label */}
        <span className="text-[11px] font-medium text-center leading-tight">{item.shortName}</span>

        {/* Badge count (only show if has count) */}
        {badgeCount > 0 && (
          <div
            className={cn(
              'absolute top-1.5 right-1.5 px-1.5 py-0.5 text-[10px] font-medium rounded-full border',
              getBadgeClasses(item.badgeColor, isActive)
            )}
          >
            {badgeCount}
          </div>
        )}
      </Link>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'absolute top-0 left-0 bottom-0 bg-card border-r border-border flex flex-col transition-all duration-300 z-50 shadow-xl',
          collapsed ? 'w-[60px]' : 'w-[240px] lg:w-[360px]'
        )}
        style={{ overflow: 'visible' }}
      >
        {/* Toggle Button */}
        <button
          onClick={() => onToggle(!collapsed)}
          className="absolute -right-3 top-1/2 -translate-y-1/2 z-50 w-6 h-6 bg-border hover:bg-accent rounded-full flex items-center justify-center transition-colors shadow-md"
        >
          {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
        </button>

        {/* Navigation Section */}
        <div className="flex-none p-3">
          {/* Navigation Grid */}
          {isExpanded ? (
            // Expanded: 2-column grid layout
            <nav className="grid grid-cols-2 gap-2">{navItems.map(renderExpandedNavItem)}</nav>
          ) : (
            // Collapsed: vertical icon list
            <nav className="flex flex-col items-center gap-1">{navItems.map(renderCollapsedNavItem)}</nav>
          )}
        </div>

        {/* Separator - full width */}
        <div className="flex-none">
          <div className="h-px bg-border" />
        </div>

        {/* Unit List Section */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-hidden">
            <RecordsUnitList collapsed={!isExpanded} />
          </div>
        </div>

        {/* Footer info when expanded */}
        {isExpanded && !loading && (
          <div className="flex-none p-3 border-t border-border text-[10px] text-muted-foreground font-mono bg-card/50">
            <div className="flex items-center justify-between">
              <span>Internal Only: <span className="text-[#8FB560]">{user?.is_internal ? 'Active' : 'Inactive'}</span></span>
              <Link to="/records/devices" className="inline-flex items-center gap-1 text-[#8FB560] hover:underline">
                <Cpu className="h-2.5 w-2.5" />
                View All Devices
              </Link>
            </div>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}
